import type {
  UiSemanticElement,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';

import {
  collectSemanticDom,
  isSemanticDomElementRendered,
  withdrawn,
} from './semantic-dom-context';
import { viewReadClock } from './view-read-clock';

/** Popups the app portals out of its regions: menus, listboxes and dialogs. */
const OVERLAY =
  '[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"]';
/** A bar that floats over what it belongs to (the tools of a selection). */
const BAR = '[role="toolbar"]';
const MAX_OVERLAYS = 4;
/** Shadow trees one read looks into for what floats in them. */
const MAX_SHADOW_TREES = 256;

/** Every open shadow tree below `root`, the trees inside them included. */
function* shadowTrees(
  root: ParentNode,
  budget: { left: number }
): Iterable<ShadowRoot> {
  for (const element of root.querySelectorAll('*')) {
    const tree = element.shadowRoot;
    if (!tree) continue;
    if (budget.left-- <= 0) return;
    yield tree;
    yield* shadowTrees(tree, budget);
  }
}

/** The element above `element` as drawn: out of a shadow tree to its host. */
function above(element: Element): Element | null {
  if (element.parentElement) return element.parentElement;
  const root = element.getRootNode();
  return 'host' in root ? (root as ShadowRoot).host : null;
}

/** Whether `element` sits inside something that is read as a popup itself. */
function nested(element: Element): boolean {
  for (let parent = above(element); parent; parent = above(parent))
    if (parent.matches(`${OVERLAY},${BAR}`)) return true;
  return false;
}

/**
 * The open popups, newest first: menus, listboxes and dialogs before floating
 * bars. A popup inside another popup is read with that popup. One a region
 * opens inside itself counts like one the app portals out: the region's read
 * is bounded and follows its markup, where a popup stands behind everything
 * the region draws. A component that draws into its own shadow tree (an
 * editor) shows its menus and bars there: these count as well.
 */
function semanticOverlays(document: Document): HTMLElement[] {
  const popups: HTMLElement[] = [];
  const bars: HTMLElement[] = [];
  for (const element of document.querySelectorAll<HTMLElement>(OVERLAY)) {
    if (element.parentElement?.closest(OVERLAY)) continue;
    popups.push(element);
  }
  for (const tree of shadowTrees(document, { left: MAX_SHADOW_TREES }))
    for (const element of tree.querySelectorAll<HTMLElement>(
      `${OVERLAY},${BAR}`
    )) {
      if (nested(element)) continue;
      (element.matches(BAR) ? bars : popups).push(element);
    }
  return [...popups.reverse(), ...bars.reverse()];
}

/** The open popups a read lists: the ones that are drawn. */
export function findSemanticOverlays(
  document: Document,
  scope: WorkspaceViewScope
): HTMLElement[] {
  return semanticOverlays(document)
    .filter(element => isSemanticDomElementRendered(element, scope))
    .slice(0, MAX_OVERLAYS);
}

/**
 * Whether a popup is laid out and kept from the user by nothing but its opacity: it fades in.
 * What it covers may be withdrawn already, so a read of this moment lists neither.
 */
export function isSemanticOverlayFading(overlay: HTMLElement): boolean {
  if (!overlay.isConnected) return false;
  const bounds = overlay.getBoundingClientRect();
  if (bounds.width <= 0 || bounds.height <= 0) return false;
  let transparent = false;
  for (
    let current: Element | null = overlay;
    current;
    current = above(current)
  ) {
    if (current.matches(withdrawn)) return false;
    const style = current.ownerDocument.defaultView?.getComputedStyle(current);
    if (
      !style ||
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse'
    )
      return false;
    if (style.opacity === '0') transparent = true;
  }
  return transparent;
}

/** The popups that fade in right now. */
export function findFadingSemanticOverlays(document: Document): HTMLElement[] {
  return semanticOverlays(document).filter(isSemanticOverlayFading);
}

/** Request-time read of the open popups within the caller's deadline. */
export async function collectSemanticOverlays(
  document: Document,
  scope: WorkspaceViewScope,
  options: { signal: AbortSignal; isCurrent: () => boolean; deadline: number }
): Promise<UiSemanticElement[]> {
  const elements: UiSemanticElement[] = [];
  for (const overlay of findSemanticOverlays(document, scope)) {
    const remaining = options.deadline - viewReadClock.now();
    if (remaining <= 0 || !options.isCurrent()) break;
    elements.push(
      ...(await collectSemanticDom(overlay, scope, {
        signal: options.signal,
        isCurrent: options.isCurrent,
        deadlineMs: remaining,
        yieldTask: () => new Promise<void>(resolve => setTimeout(resolve, 0)),
      }))
    );
  }
  return elements;
}
